import { useEffect, useRef } from 'react'
import { useContentFStore, streamContentF } from '@/stores/useContentFStore'
import { ContentFChatMessage } from './ContentFChatMessage'
import { ContentFChatInput } from './ContentFChatInput'
import { ContentFThinkingIndicator } from './ContentFThinkingIndicator'
import { ContentFThinkingBlock } from './ContentFThinkingBlock'

export function ContentFChat() {
  const {
    sessionId,
    sourceCpSessionId,
    turns,
    workflowStatus,
    streamingThinking,
    streamingResponse,
    isThinking,
    error,
    foolscapJson,
    entityName,
    entityType,
    // Per-node state
    evaluatorIsProcessing,
    evaluatorIsThinking,
    evaluatorStreamingThinking,
    editorIsProcessing,
    editorIsThinking,
    editorStreamingThinking,
    responderIsProcessing,
    responderIsThinking,
    responderStreamingThinking,
    // Actions
    setWorkflowStatus,
    setError,
    addUserTurn,
    startStreaming,
    startThinking,
    updateThinking,
    finishThinking,
    updateStreamingResponse,
    finishStreaming,
    setSurfacedCards,
    updateFoolscapJson,
    updateFoolscapMarkdown,
    updateFoolscapMarkdownAnnotated,
    // Per-node actions
    startEvaluatorProcessing,
    startEvaluatorThinking,
    updateEvaluatorThinking,
    finishEvaluatorThinking,
    finishEvaluatorProcessing,
    startEditorProcessing,
    startEditorThinking,
    updateEditorThinking,
    finishEditorThinking,
    finishEditorProcessing,
    startResponderProcessing,
    startResponderThinking,
    updateResponderThinking,
    finishResponderThinking,
    finishResponderProcessing,
    resetNodeState,
  } = useContentFStore()

  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when new messages arrive or content is streaming
  useEffect(() => {
    const isStreaming = workflowStatus === 'streaming' || workflowStatus === 'processing'
    messagesEndRef.current?.scrollIntoView({
      behavior: isStreaming ? 'instant' : 'smooth',
      block: 'end'
    })
  }, [turns, streamingThinking, streamingResponse, isThinking, workflowStatus,
      evaluatorStreamingThinking, editorStreamingThinking, responderStreamingThinking])

  // Auto-clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [error, setError])

  const handleSubmit = async (content: string) => {
    if (!content.trim() || workflowStatus === 'submitting' || workflowStatus === 'processing' || workflowStatus === 'streaming') {
      return
    }

    if (!sessionId) {
      setError('No session ID available')
      return
    }

    addUserTurn(content)
    setWorkflowStatus('submitting')

    try {
      startStreaming()
      resetNodeState()

      let fullResponse = ''

      await streamContentF(
        {
          content,
          session_id: sessionId,
          source_cp_session_id: sourceCpSessionId,
          current_foolscap_json: foolscapJson,
          entity_name: entityName || undefined,
          entity_type: entityType || undefined,
        },
        {
          // Legacy single-node callbacks (backwards compat)
          onThinkingStart: () => startThinking(),
          onThinkingDelta: (delta) => updateThinking(delta),
          onThinkingComplete: () => finishThinking(),

          // Evaluator callbacks
          onEvaluatorStart: () => startEvaluatorProcessing(),
          onEvaluatorThinkingStart: () => startEvaluatorThinking(),
          onEvaluatorThinkingDelta: (delta) => updateEvaluatorThinking(delta),
          onEvaluatorThinkingComplete: () => finishEvaluatorThinking(),
          onEvaluatorDone: () => finishEvaluatorProcessing(),

          // Editor callbacks
          onEditorStart: () => startEditorProcessing(),
          onEditorThinkingStart: () => startEditorThinking(),
          onEditorThinkingDelta: (delta) => updateEditorThinking(delta),
          onEditorThinkingComplete: () => finishEditorThinking(),
          onEditorDone: () => finishEditorProcessing(),

          // Responder callbacks
          onResponderStart: () => startResponderProcessing(),
          onResponderThinkingStart: () => startResponderThinking(),
          onResponderThinkingDelta: (delta) => updateResponderThinking(delta),
          onResponderThinkingComplete: () => finishResponderThinking(),
          onResponderDone: () => finishResponderProcessing(),

          // Response and document updates
          onResponseDelta: (delta) => {
            fullResponse += delta
            updateStreamingResponse(delta)
          },
          onCardsSurfaced: (cards) => setSurfacedCards(cards),
          onDocumentUpdate: (json, markdown, annotatedMarkdown) => {
            updateFoolscapJson(json)
            updateFoolscapMarkdown(markdown)
            updateFoolscapMarkdownAnnotated(annotatedMarkdown)
          },
          onDone: (output) => {
            finishStreaming(output)
          },
          onError: (err) => {
            setError(err)
          },
        }
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send message')
    }
  }

  return (
    <div className="h-full flex flex-col">
      {/* Messages area */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-0 md:pr-6 scroll-touch overscroll-contain">
        <div className="max-w-2xl py-4 md:py-6 mx-auto md:mx-0">
          {turns.length === 0 && workflowStatus !== 'submitting' && workflowStatus !== 'streaming' ? (
            <div className="text-center py-12">
              <h2 className="text-lg font-light text-foreground mb-2">
                Content Foolscap Studio
              </h2>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Build your one-page story structure using the Sovereign Entity Framework.
                Start by telling me about your entity (hero).
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {turns.map((turn, index) => (
                <ContentFChatMessage key={index} turn={turn} />
              ))}

              {/* Submitting indicator */}
              {workflowStatus === 'submitting' && (
                <ContentFThinkingIndicator />
              )}

              {/* Per-node streaming thinking blocks */}
              {(workflowStatus === 'streaming' || workflowStatus === 'processing') && (
                <div className="flex gap-4">
                  <div className="flex-1 min-w-0 space-y-3">
                    {/* Evaluator thinking */}
                    {evaluatorStreamingThinking && (
                      <ContentFThinkingBlock
                        thinking={evaluatorStreamingThinking}
                        isStreaming={evaluatorIsThinking}
                        isProcessing={evaluatorIsProcessing}
                        label="Evaluator"
                        color="text-amber-600"
                      />
                    )}
                    {evaluatorIsProcessing && !evaluatorStreamingThinking && (
                      <div className="text-[10px] font-light uppercase tracking-[0.08em] text-amber-600 flex items-center gap-2">
                        <span className="inline-block w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                        <span>Evaluating</span>
                      </div>
                    )}

                    {/* Editor thinking */}
                    {editorStreamingThinking && (
                      <ContentFThinkingBlock
                        thinking={editorStreamingThinking}
                        isStreaming={editorIsThinking}
                        isProcessing={editorIsProcessing}
                        label="Editor"
                        color="text-rose-600"
                      />
                    )}
                    {editorIsProcessing && !editorStreamingThinking && (
                      <div className="text-[10px] font-light uppercase tracking-[0.08em] text-rose-600 flex items-center gap-2">
                        <span className="inline-block w-2 h-2 bg-rose-500 rounded-full animate-pulse" />
                        <span>Editing</span>
                      </div>
                    )}

                    {/* Responder thinking */}
                    {responderStreamingThinking && (
                      <ContentFThinkingBlock
                        thinking={responderStreamingThinking}
                        isStreaming={responderIsThinking}
                        isProcessing={responderIsProcessing}
                        label="Responder"
                        color="text-violet-600"
                      />
                    )}
                    {responderIsProcessing && !responderStreamingThinking && (
                      <div className="text-[10px] font-light uppercase tracking-[0.08em] text-violet-600 flex items-center gap-2">
                        <span className="inline-block w-2 h-2 bg-violet-500 rounded-full animate-pulse" />
                        <span>Composing response</span>
                      </div>
                    )}

                    {/* Fallback: legacy single thinking (backwards compat) */}
                    {streamingThinking && !evaluatorStreamingThinking && !editorStreamingThinking && !responderStreamingThinking && (
                      <ContentFThinkingBlock
                        thinking={streamingThinking}
                        isStreaming={isThinking}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Streaming response */}
              {streamingResponse && (
                <ContentFChatMessage
                  turn={{
                    role: 'assistant',
                    content: streamingResponse,
                    timestamp: new Date().toISOString(),
                  }}
                  isStreaming
                />
              )}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="px-4 md:px-0 md:pr-6">
          <div className="max-w-2xl mx-auto md:mx-0 py-2 px-3 bg-destructive/10 border border-destructive/20 rounded text-sm text-destructive">
            {error}
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="flex-shrink-0 py-3 md:py-4 px-4 md:px-0 md:pr-6 border-t border-border md:border-0">
        <div className="max-w-2xl mx-auto md:mx-0">
          <ContentFChatInput
            onSubmit={handleSubmit}
            disabled={workflowStatus === 'submitting' || workflowStatus === 'streaming'}
            placeholder={
              turns.length === 0
                ? "Tell me about the entity you want to write about..."
                : "Continue the conversation..."
            }
          />
        </div>
      </div>
    </div>
  )
}
